import { useCallback, useState } from "react";

import axios from "axios";

import { getErrorMessage } from "~/v2/api/hooks";
import type { OrderFill } from "~/v3-poc/api/orderFills.schema";
import type { OrderDetailDialogProps } from "./OrderDetailDialog.connector";
import {
  mapOrderDetail,
  type OrderDetailViewModel,
} from "./OrderDetailDialog.utils";
import { getOrderFillsPage } from "./orderFills.utils";
import type { ReplacementHistory } from "./replacementChains";
import { useAtdOrderDetail } from "./useAtdOrderDetail";
import { useAtdOrderFills } from "./useAtdOrderFills";
import { useAtdReplacementChain } from "./useAtdReplacementChain";

export interface OrderDetailControllerResult {
  open: boolean;
  detail?: OrderDetailViewModel;
  title: string;
  description: string;
  replacementHistory?: ReplacementHistory;
  fills: OrderFill[];
  isLoading: boolean;
  isFetching: boolean;
  isHistoryLoading: boolean;
  isHistoryFetching: boolean;
  isFillsLoading: boolean;
  isFillsFetching: boolean;
  isFillsError: boolean;
  fillsErrorMessage?: string;
  fillsPageNumber: number;
  hasPreviousFillsPage: boolean;
  hasNextFillsPage: boolean;
  isError: boolean;
  isNotFound: boolean;
  errorMessage?: string;
  onOpenChange: (open: boolean) => void;
  onCloseAutoFocus?: OrderDetailDialogProps["onCloseAutoFocus"];
  onRetry: () => void;
  onRetryHistory: () => void;
  onRetryFills: () => void;
  onPreviousFillsPage: () => void;
  onNextFillsPage: () => void;
}

interface FillsPaginationState {
  contextKey: string;
  cursors: string[];
}

const EMPTY_CURSOR_HISTORY: string[] = [];

export const useOrderDetailController = (
  props: OrderDetailDialogProps,
): OrderDetailControllerResult => {
  const { open, accountID, product, orderID, onOpenChange, onCloseAutoFocus } =
    props;
  const fillsContextKey = JSON.stringify({ accountID, product, orderID });
  const [fillsPagination, setFillsPagination] = useState<FillsPaginationState>({
    contextKey: fillsContextKey,
    cursors: [],
  });
  const fillsCursorHistory =
    fillsPagination.contextKey === fillsContextKey
      ? fillsPagination.cursors
      : EMPTY_CURSOR_HISTORY;
  const fillsPageToken = fillsCursorHistory.at(-1);

  const detailQuery = useAtdOrderDetail({
    accountID,
    product,
    orderID,
    enabled: open,
  });
  const historyQuery = useAtdReplacementChain({
    accountID,
    product,
    orderID,
    selectedOrder: detailQuery.data,
    enabled: open && Boolean(detailQuery.data),
  });
  const fillsQuery = useAtdOrderFills({
    accountID,
    product,
    orderID,
    pageToken: fillsPageToken,
    enabled: open,
  });
  const { fills, nextToken } = getOrderFillsPage(fillsQuery.data);
  const isNotFound =
    axios.isAxiosError(detailQuery.error) &&
    detailQuery.error.response?.status === 404;
  const detail = detailQuery.data
    ? mapOrderDetail(detailQuery.data)
    : undefined;
  const title = detail?.title ?? "Order details";
  const description =
    detail?.description ??
    "Focused order details with bounded replacement-version history and paginated fills for this order.";

  const onPreviousFillsPage = useCallback(() => {
    setFillsPagination((current) => {
      if (current.contextKey !== fillsContextKey) {
        return { contextKey: fillsContextKey, cursors: [] };
      }
      return {
        contextKey: fillsContextKey,
        cursors: current.cursors.slice(0, -1),
      };
    });
  }, [fillsContextKey]);

  const onNextFillsPage = useCallback(() => {
    if (!nextToken) {
      return;
    }
    setFillsPagination((current) => ({
      contextKey: fillsContextKey,
      cursors: [
        ...(current.contextKey === fillsContextKey ? current.cursors : []),
        nextToken,
      ],
    }));
  }, [fillsContextKey, nextToken]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setFillsPagination({ contextKey: fillsContextKey, cursors: [] });
      }
      onOpenChange(nextOpen);
    },
    [fillsContextKey, onOpenChange],
  );

  return {
    open,
    detail,
    title,
    description,
    replacementHistory: historyQuery.data ?? undefined,
    fills,
    isLoading: open && detailQuery.isLoading,
    isFetching: open && detailQuery.isFetching,
    isHistoryLoading:
      open && Boolean(detailQuery.data) && historyQuery.isLoading,
    isHistoryFetching:
      open && Boolean(detailQuery.data) && historyQuery.isFetching,
    isFillsLoading: open && fillsQuery.isLoading,
    isFillsFetching: open && fillsQuery.isFetching,
    isFillsError: open && fillsQuery.isError,
    fillsErrorMessage: fillsQuery.error
      ? getErrorMessage(fillsQuery.error)
      : undefined,
    fillsPageNumber: fillsCursorHistory.length + 1,
    hasPreviousFillsPage: fillsCursorHistory.length > 0,
    hasNextFillsPage: Boolean(nextToken),
    isError: open && detailQuery.isError,
    isNotFound: open && isNotFound,
    errorMessage: detailQuery.error?.message,
    onOpenChange: handleOpenChange,
    onCloseAutoFocus,
    onRetry: () => {
      void detailQuery.refetch();
    },
    onRetryHistory: () => {
      void historyQuery.refetch();
    },
    onRetryFills: () => {
      void fillsQuery.refetch();
    },
    onPreviousFillsPage,
    onNextFillsPage,
  };
};
